import { memo, useMemo } from 'react';
import type { DescriptionPanelCollectionContents } from './domain/DescriptionPanelCollectionContents';
import { DescriptionPanelView } from './components';
import type { SelectedItemSet } from '@view-model/models/common/basic';
import type { ModelElementId } from '@view-model/domain/key';
import type { DescriptionPanelId, ModelId, ViewModelId } from 'balus-common';
import type { DragContext } from '@model-framework/ui';

type Props = {
  viewModelId: ViewModelId;
  modelId: ModelId;
  panelContents: DescriptionPanelCollectionContents;
  selectedItems: SelectedItemSet<ModelElementId>;
  readonly: boolean;
  onClick(id: DescriptionPanelId, e: React.MouseEvent<SVGGElement>): void;
  onDragStart(id: DescriptionPanelId, e: React.MouseEvent<SVGGElement>): void;
  onDrag(context: DragContext): void;
  onDragEnd(context: DragContext): void;
};

export const DescriptionPanelCollectionView = memo(
  ({
    viewModelId,
    modelId,
    panelContents,
    selectedItems,
    readonly,
    onClick,
    onDragStart,
    onDrag,
    onDragEnd,
  }: Props) => {
    const orderedPanelEntries = useMemo(() => {
      return panelContents.orderedEntries(selectedItems);
    }, [panelContents, selectedItems]);

    return (
      <>
        {orderedPanelEntries.map(([panel, position]) => {
          const isSelected = selectedItems.isSelected(panel.id);

          return (
            <g key={panel.id} data-model-element-id={panel.id}>
              <DescriptionPanelView
                viewModelId={viewModelId}
                modelId={modelId}
                id={panel.id}
                position={position}
                isSelected={isSelected}
                readonly={readonly}
                onClick={onClick}
                onDragStart={onDragStart}
                onDrag={onDrag}
                onDragEnd={onDragEnd}
              />
            </g>
          );
        })}
      </>
    );
  }
);
DescriptionPanelCollectionView.displayName = 'DescriptionPanelCollectionView';
